import { track } from '@eventbrite/datalayer-library';
import type { ComponentProps } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { loadDatesForMonth } from '../../actions/eventDates';
import {
    setSection,
    setSelectedSessionId,
    setSelectedTime,
    setShouldShowTicketError,
    setShouldShowTimeError,
    type InfiniteCalendarSeriesDispatcher,
} from '../../actions/infiniteCalendarSeries';
import { HeapEventName } from '../../constants/heap/events';
import {
    getIsAbandonOrderAttemptPage,
    getOnCloseFn,
} from '../../containers/connectPage';
import { getTemplateProps } from '../../containers/template';
import {
    getAppLocale,
    getCurrentTimezone,
    getIsAppRebrandActive,
    getIsModal,
    isNativeApp,
} from '../../selectors/app';
import {
    getCurrency,
    getEventSalesStatus,
    getIsEventOnline,
} from '../../selectors/event';
import {
    getActiveSection,
    getCalendarDates,
    getCalendarType,
    getCompactCalendarData,
    getFirstEventDateString,
    getFirstSessionStartDateTime,
    getIsTicketSectionLoading,
    getIsTimeSectionLoading,
    getParentEventInfo,
    getScrollCalendarData,
    getSelectedDate,
    getSelectedSessionId,
    getSelectedTime,
    getShouldShowTicketError,
    getShouldShowTimeError,
    getTimeSlots,
    getTimezone,
} from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import type { CheckoutState } from '../../state';
import {
    AccordionSection,
    CalendarType,
    type InfiniteCalendarSeriesNamespace,
} from '../../state/infiniteCalendarSeries';
import { normalizeLocale } from '../../utils/general';
import { DATE_FORMATS } from './constants';
import {
    handleDateSelect,
    handleTicketSelectionFlow,
} from './handleDateSelect';
import { InfiniteCalendarSeries } from './InfiniteCalendarSeries';
import { getFormattedDate } from './utils';

const createSharedPropsForHeap = (calendarType: CalendarType | undefined) => ({
    isSeriesInfiniteCalendar: true,
    calendarType,
});

export const mapStateToProps = (
    state: CheckoutState,
): InfiniteCalendarSeriesNamespace => {
    const locale = normalizeLocale(getAppLocale(state));
    const timezone = getTimezone(state);
    const isOnlineEvent = getIsEventOnline(state);
    const parentEventInfo = getParentEventInfo(state);
    const firstDateFromServerContext = getFirstEventDateString(state);
    const firstSessionStartDateTime = getFirstSessionStartDateTime(state);
    const currency = getCurrency(state);
    const firstMonth = getFormattedDate({
        isoDate: firstDateFromServerContext,
        timezone,
    }).date;
    const lastMonth = getFormattedDate({
        isoDate: parentEventInfo.endUTCDate,
        timezone,
    }).date;

    return {
        activeSection: getActiveSection(state),
        calendarDates: getCalendarDates(state),
        calendarType: getCalendarType(state),
        compactCalendarData: getCompactCalendarData(state),
        currentTimezone: getCurrentTimezone(state) ?? null,
        eventDateOverride: getFormattedDate({
            isoDate: firstSessionStartDateTime,
            timezone,
        }).format(locale, DATE_FORMATS.SHORT_DATE),
        currency,
        eventStatus: getEventSalesStatus(state)?.eventSalesStatus?.messageCode,
        firstMonth: {
            month: firstMonth.getMonth(),
            year: firstMonth.getFullYear(),
        },
        isAppRebrandActive: getIsAppRebrandActive(state),
        isEventOnline: isOnlineEvent,
        isModal: getIsModal(state) ?? false,
        isNativeApp: isNativeApp(state),
        isTicketSectionLoading: getIsTicketSectionLoading(state),
        isTimeSectionLoading: getIsTimeSectionLoading(state),
        lastMonth: {
            month: lastMonth.getMonth(),
            year: lastMonth.getFullYear(),
        },
        locale,
        parentEventInfo,
        scrollCalendarData: getScrollCalendarData(state),
        selectedDate: getSelectedDate(state),
        selectedSessionId: getSelectedSessionId(state),
        selectedTime: getSelectedTime(state),
        shouldShowTicketError: getShouldShowTicketError(state),
        shouldShowTimeError: getShouldShowTimeError(state),
        templateProps: getTemplateProps({ state, formName: '' }),
        timeSlots: getTimeSlots(state),
        timezone,
    };
};

export const mapDispatchToProps = (
    dispatch: Dispatch<CheckoutState>,
    ownProps: any,
): InfiniteCalendarSeriesDispatcher => {
    const { router } = ownProps;
    const isAbandonOrderAttemptPage = getIsAbandonOrderAttemptPage(router);

    return {
        onPageClose: getOnCloseFn(router, isAbandonOrderAttemptPage, dispatch),
        onDateSelect: (dateString, calendarType, id, sessionTime) => {
            dispatch(setSelectedTime(''));
            handleDateSelect({
                dateString,
                calendarType,
                eventId: id,
                sessionTime,
                dispatch,
                track,
            });
        },
        onTimeSelect: async (eventId, sessionLabel, calendarType) => {
            const sharedProps = createSharedPropsForHeap(calendarType);
            track({
                eventName: HeapEventName.timedEntrySessionSelectorClick,
                eventData: {
                    selectedOption: sessionLabel,
                    ...sharedProps,
                },
            });
            dispatch(setShouldShowTicketError(false));
            dispatch(setSelectedTime(sessionLabel));
            await handleTicketSelectionFlow({ eventId, dispatch });
        },
        onMonthLoad: async (month, year) => {
            dispatch(loadDatesForMonth(month, year));
        },
        resetTimeSelection: () => {
            dispatch(setSelectedTime(''));
            dispatch(setSelectedSessionId(''));
        },
        onToggleSection: ({
            activeSection,
            selectedDate,
            selectedTime,
            calendarType,
        }) => {
            const sharedProps = createSharedPropsForHeap(calendarType);
            const isCompactWithSessionCalendar =
                calendarType === CalendarType.compactWithSession;
            const isSingleDayEvent =
                calendarType === CalendarType.singleDayEvents;

            if (isSingleDayEvent && !selectedTime) {
                dispatch(setShouldShowTicketError(true));
                return;
            }

            if (activeSection === AccordionSection.date) {
                dispatch(setSelectedTime(''));
                dispatch(setSelectedSessionId(''));
                track({
                    eventName:
                        HeapEventName.timedEntryDateAccordionSectionClick,
                    eventData: sharedProps,
                });
            } else if (activeSection === AccordionSection.time) {
                track({
                    eventName:
                        HeapEventName.timedEntrySessionAccordionSectionClick,
                    eventData: sharedProps,
                });
            }

            if (isCompactWithSessionCalendar) {
                if (
                    activeSection === AccordionSection.tickets &&
                    !selectedDate
                ) {
                    dispatch(setShouldShowTicketError(true));
                    return;
                }
                dispatch(setSection(activeSection));
                return;
            }

            if (activeSection === AccordionSection.time && !selectedDate) {
                dispatch(setShouldShowTimeError(true));
                dispatch(setShouldShowTicketError(false));
            } else if (
                activeSection === AccordionSection.tickets &&
                !selectedTime
            ) {
                dispatch(setShouldShowTicketError(true));
                dispatch(setShouldShowTimeError(false));
            } else {
                dispatch(setShouldShowTimeError(false));
                dispatch(setShouldShowTicketError(false));
                dispatch(setSection(activeSection));
            }
        },
    };
};

export const mergeProps = (
    stateProps: InfiniteCalendarSeriesNamespace,
    dispatchProps: InfiniteCalendarSeriesDispatcher,
    ownProps: ComponentProps<typeof InfiniteCalendarSeries>,
) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
});

export const connectInfiniteCalendarSeries = () =>
    connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps,
    )(InfiniteCalendarSeries);

export const ConnectedInfiniteCalendarSeries = connectInfiniteCalendarSeries();
